// Gallery.js - Ceylon Wayfinder Premium Taxi Service
import React, { useState, useEffect, useRef } from 'react';
import './gallery.css';
import { FaExpand, FaTimes, FaChevronLeft, FaChevronRight, FaHeart, FaShare, FaDownload, FaStar, FaShieldAlt, FaClock } from 'react-icons/fa';

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleImages, setVisibleImages] = useState({});
  const galleryRef = useRef(null);

  // Gallery images data - Ceylon Wayfinder Fleet & Services
  const galleryImages = [
    // Premium Fleet Images
    {
      id: 1,
      src: '/img1.jpeg',
      thumbnail: '/img1.jpeg',
      title: 'Premium Sedan Fleet',
      description: 'Explore the wonders of Sigiriya in comfort with our premium sedan fleet. Travel effortlessly to iconic landmarks like the Sigiriya Rock Fortress while enjoying a smooth and stylish ride.',
      location: 'Sigiriya',
      date: '2021',
      featured: true
    },
    {
      id: 2,
      src: '/img2.jpeg',
      thumbnail: '/img2.jpeg',
      title: 'Luxury SUV Service',
      description: 'Spacious SUVs perfect for family trips and group travel across Sri Lanka',
      location: 'Kandy',
      date: '2024'
    },
    {
      id: 3,
      src: '/img3.jpeg',
      thumbnail: '/img3.jpeg',
      title: 'Executive Van',
      description: 'Comfortable vans for larger groups, ideal for tours and excursions',
      location: 'Trinco',
      date: '2023'
    },
    {
      id: 4,
      src: '/img4.jpeg',
      thumbnail: '/img4.jpeg',
      title: 'Coconut Tree Hill Escape – Mirissa',
      description: 'Experience the iconic Coconut Tree Hill in Mirissa, where endless ocean views meet swaying palm trees. A perfect spot to relax, capture stunning photos, and enjoy Sri Lanka’s coastal beauty.',
      location: 'Mirissa, Coconut Tree Hill',
      date: '2023',
      featured: true
    },
    {
      id: 5,
      src: '/img5.webp',
      thumbnail: '/img5.webp',
      title: 'Matale Travel Experience',
      description: 'Explore the charm of Matale, known for its lush greenery, spice gardens, and cultural heritage. Enjoy a peaceful journey through one of Sri Lanka’s most scenic regions.',
      location: 'Matale',
      date: '2021'
    },
    {
      id: 6,
      src: '/img6.jpeg',
      thumbnail: '/img6.jpeg',
      title: 'Professional Drivers',
      description: 'Experienced, licensed, and background-checked professional drivers',
      location: 'Hatton',
      date: '2023'
    },
    {
      id: 7,
      src: '/img7.jpeg',
      thumbnail: '/img7.jpeg',
      title: 'City Tours',
      description: 'Explore Sri Lanka\'s beautiful cities with our guided tour services',
      location: 'Galle',
      date: '2022'
    },
    {
      id: 8,
      src: '/img8.jpeg',
      thumbnail: '/img8.jpeg',
      title: 'Colombo City Tour',
      description: 'Experience the vibrant culture and bustling streets of Colombo with our city tour services. Discover hidden gems, historical landmarks, and vibrant markets while enjoying the comfort and reliability of Ceylon Wayfinder\'s premium transportation.',
      location: 'Colombo',
      date: '2025',
      featured: true
    },
    {
      id: 9,
      src: '/img9.jpeg',
      thumbnail: '/img9.jpeg',
      title: 'Round Tour',
      description: 'Embark on a complete round tour across Sri Lanka, covering iconic destinations like Kandy, scenic landscapes, cultural landmarks, and coastal attractions for a truly unforgettable journey.',
      location: 'Kandy',
      date: '2024'
    },
    {
      id: 10,
      src: '/img10.jpeg',
      thumbnail: '/img10.jpeg',
      title: 'Airport Transfer Service',
      description: 'Reliable pickup and drop-off at Bandaranaike International Airport',
      location: 'Airport',
      date: '2024'
    },
    {
      id: 11,
      src: '/img11.jpeg',
      thumbnail: '/img11.jpeg',
      title: 'Business Travel in Kandy',
      description: 'Professional transportation solutions for business travelers in Kandy, ensuring comfort, punctuality, and reliability.',
      location: 'Kandy',
      date: '2024'
    },
    {
      id: 12,
      src: '/img12.jpeg',
      thumbnail: '/img12.jpeg',
      title: 'Airport Transfer Service',
      description: 'Reliable pickup and drop-off at Bandaranaike International Airport',
      location: 'Airport',
      date: '2025'
    },
    {
      id: 13,
      src: '/img13.jpeg',
      thumbnail: '/img13.jpeg',
      title: 'Round Tour - Trinco',
      description: 'Visit ancient cities and cultural heritage sites comfortably',
      location: 'Trinco',
      date: '2022'
    },
    {
      id: 14,
      src: '/img14.jpeg',
      thumbnail: '/img14.jpeg',
      title: 'Temple of the Sacred Tooth Relic',
      description: 'Visit the revered Temple of the Sacred Tooth Relic, a UNESCO World Heritage site in Kandy, known for its deep cultural and spiritual significance.',
      location: 'Temple of the Sacred Tooth Relic',
      date: '2025'
    },
    {
      id: 15,
      src: '/img15.jpeg',
      thumbnail: '/img15.jpeg',
      title: 'Tea Plantation Tours',
      description: 'Scenic drives through Sri Lanka\'s famous tea plantations',
      location: 'Nuwaraeliya',
      date: '2023',
      featured: true
    }
  ];

  // Featured images for hero section
  const featuredImages = galleryImages.filter(img => img.featured);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const imgId = entry.target.dataset.id;
            setVisibleImages(prev => ({ ...prev, [imgId]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    document.querySelectorAll('.gallery-item').forEach(item => {
      observer.observe(item);
    });

    return () => observer.disconnect();
  }, []);

  // Simulate loading
  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1500);
  }, []);

  // Navigation functions
  const handlePrevImage = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    setSelectedImage(galleryImages[prevIndex]);
  };

  const handleNextImage = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryImages[nextIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  return (
    <div className="gallery-container" ref={galleryRef}>
      
      

      {/* Hero Section */}
      <section className="gallery-hero">
        <div className="gallery-hero-slideshow">
          {featuredImages.slice(0, 4).map((img, index) => (
               <img 
          src="/bannerceylonwayfinder.png" 
          alt="Ceylon Wayfinder Premium Taxi Service" 
          className="aboutus-hero-image"
        />

          ))}
        </div>
        <div className="gallery-hero-overlay"></div>
        <div className="gallery-hero-content">
          <span className="hero-subtitle">Our Journey</span>
          <h1 className="hero-title">
            Ceylon <span>Wayfinder Gallery</span>
          </h1>
          <p className="hero-description">
            Experience the comfort, reliability, and excellence of Ceylon Wayfinder's 
            premium transportation services across beautiful Sri Lanka.
          </p>
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="stat-number">50+</span>
              <span className="stat-label">Vehicles</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">10k+</span>
              <span className="stat-label">Happy Riders</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="mouse"></div>
          <span>Explore Gallery</span>
        </div>
      </section>

      {/* Gallery Grid Section */}
      <section className="gallery-grid-section">
        <div className="gallery-header">
          <h2 className="gallery-title">Our Fleet & Services</h2>
          <p className="gallery-subtitle">Explore our premium vehicles and transportation services across Sri Lanka</p>
        </div>
        
        <div className="gallery-grid">
          {galleryImages.map((image, index) => (
            <div
              key={image.id}
              className={`gallery-item ${image.featured ? 'featured' : ''}`}
              data-id={image.id}
              onClick={() => setSelectedImage(image)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {visibleImages[image.id] && (
                <>
                  <img 
                    src={image.src} 
                    alt={image.title}
                    loading="lazy"
                  />
                  <div className="gallery-item-overlay">
                    <div className="item-content">
                      <h3 className="item-title">{image.title}</h3>
                      <p className="item-description">{image.description}</p>
                      <div className="item-meta">
                        <span><FaStar className="meta-icon" /> {image.location}</span>
                        <span><FaClock className="meta-icon" /> {image.date}</span>
                      </div>
                      <button className="item-expand">
                        <FaExpand /> View Details
                      </button>
                    </div>
                  </div>
                  {image.featured && (
                    <div className="featured-badge">
                      <FaStar /> Featured
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </section>







      {/* CTA Section */}
      <section className="gallery-cta">
        <div className="cta-content">
          <h2>Ready to Experience Premium Travel?</h2>
          <p>Book your ride with Ceylon Wayfinder today and enjoy comfortable, reliable transportation across Sri Lanka</p>
          <button className="cta-button" onClick={() => window.location.href = '/booking'}>
            Book Your Ride Now
          </button>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="gallery-lightbox" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>
              <FaTimes />
            </button>
            <button className="lightbox-prev" onClick={handlePrevImage}>
              <FaChevronLeft />
            </button>
            <button className="lightbox-next" onClick={handleNextImage}>
              <FaChevronRight />
            </button>
            
            <div className="lightbox-image-container">
              <img src={selectedImage.src} alt={selectedImage.title} />
            </div>
            
            <div className="lightbox-details">
              <h2>{selectedImage.title}</h2>
              <p>{selectedImage.description}</p>
              <div className="lightbox-meta">
                <span><FaStar className="meta-icon" /> {selectedImage.location}</span>
                <span><FaClock className="meta-icon" /> {selectedImage.date}</span>
              </div>
              <div className="lightbox-actions">
                <button className="action-btn">
                  <FaDownload /> Download
                </button>
                <button className="action-btn">
                  <FaShare /> Share
                </button>
                <button className="action-btn">
                  <FaHeart /> Favorite
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;